import { Injectable, inject, signal, effect, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { ToastService } from './toast.service';
import { PushNotificationService } from './push-notification.service';
import { NicheService } from './niche.service';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { setUser } from '@sentry/angular';

interface AuthResult {
  success: boolean;
  errorCode?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  // ---------- Private signals ----------
  private _isLoggedIn = signal(false);
  private _email = signal('');
  private _apiKey = signal('');
  private _userId = signal('');
  private _nicheId = signal('');
  private _locale = signal('en');
  private _initialized = signal(false);

  // ---------- Readonly public getters ----------
  get isLoggedIn() { return this._isLoggedIn.asReadonly(); }
  get email() { return this._email.asReadonly(); }
  get apiKey() { return this._apiKey.asReadonly(); }
  get userId() { return this._userId.asReadonly(); }
  get nicheId() { return this._nicheId.asReadonly(); }
  get locale() { return this._locale.asReadonly(); }
  get initialized() { return this._initialized.asReadonly(); }

  private http = inject(HttpClient);
  private toast = inject(ToastService);
  private pushService = inject(PushNotificationService);
  private router = inject(Router);
  // ⚠️  Avoid circular dependency: NicheService → AuthService → NicheService.
  //     Instead of injecting NicheService directly, resolve it lazily
  //     through the Injector inside the methods that need it.
  private injector = inject(Injector);

  private readonly STORAGE_KEY = 'rtila_auth';

  constructor() {
    // Reactively sync Sentry user context with auth state
    effect(() => {
      const loggedIn = this._isLoggedIn();
      const email = this._email();
      const userId = this._userId();
      const nicheId = this._nicheId();

      if (loggedIn && userId) {
        setUser({
          id: userId,
          email: email || undefined,
          nicheId: nicheId || undefined,
        });
      } else {
        setUser(null);
      }
    }, { injector: this.injector });
  }

  async restoreSession(): Promise<void> {
    const isNative = Capacitor.isNativePlatform();
    try {
      let apiKeyFromSecure: string | null = null;

      // 🔒 Native: read API key from secure Capacitor Preferences
      if (isNative) {
        const { value } = await Preferences.get({ key: 'rtila_api_key' });
        apiKeyFromSecure = value || null;
      }

      // Restore non-sensitive data from localStorage
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.email) {
          this._email.set(parsed.email);
        }
        if (parsed.nicheId) {
          this._nicheId.set(parsed.nicheId);
        }

        // Migration: if secure storage doesn't have the API key but localStorage
        // still holds it (legacy native install), migrate it now.
        if (!apiKeyFromSecure && isNative && parsed.apiKey) {
          apiKeyFromSecure = parsed.apiKey;
          try {
            await Preferences.set({ key: 'rtila_api_key', value: parsed.apiKey });
          } catch (e) {
            console.error('Failed to migrate apiKey to secure storage', e);
          }
          // Remove the sensitive key from the localStorage payload
          delete parsed.apiKey;
          try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(parsed));
          } catch {}
        }
      }

      if (apiKeyFromSecure) {
        this._apiKey.set(apiKeyFromSecure);
      }

      const valid = await this.checkAuth();
      if (!valid) {
        await this.clearPersistedSession();
        this.clear();
      } else {
        // Ensure niche data is loaded immediately after a valid session is restored.
        // This prevents the "empty home" scenario when the app starts (or resumes)
        // with an authenticated user.
        const nicheService = this.injector.get(NicheService);
        try {
          await nicheService.loadNiches();
        } catch (nicheErr) {
          console.error('Failed to load niches after session restore', nicheErr);
        }
      }
    } catch {
      await this.clearPersistedSession();
      this.clear();
    } finally {
      this._initialized.set(true);
    }
  }

  async checkAuth(): Promise<boolean> {
    try {
      const resp: any = await lastValueFrom(this.http.get('/auth/me'));
      if (resp.success) {
        this._isLoggedIn.set(true);
        this._email.set(resp.user.email);
        this._apiKey.set(resp.user.apiKey);
        this._userId.set(resp.user.id);
        this._nicheId.set(resp.user.nicheId || ''); // fixed: camelCase matching backend response
        this._locale.set(resp.user.locale || 'en');
        await this.persistSession();
        return true;
      }
    } catch { }
    return false;
  }

  async login(email: string, password: string, turnstileToken: string): Promise<AuthResult> {
    try {
      const resp: any = await lastValueFrom(
        this.http.post('/auth/login', { email, password, turnstileToken })
      );
      if (resp.success) {
        this._isLoggedIn.set(true);
        this._email.set(resp.user.email);
        this._userId.set(resp.user.id);
        this._apiKey.set(resp.user.apiKey);
        this._nicheId.set(resp.user.nicheId || ''); // fixed: camelCase
        this._locale.set(resp.user.locale || 'en');

        await this.persistSession();

        // Ensure niche data is available immediately after login
        const nicheService = this.injector.get(NicheService);
        try {
          await nicheService.loadNiches();
        } catch (nicheErr) {
          console.error('Failed to load niches after login', nicheErr);
        }

        if (this.pushService.ready()) {
          const platform = Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web';
          await this.pushService.registerDevice(platform);
        }
        return { success: true };
      } else {
        throw { error: { error_code: resp.error_code, error: resp.error || '' } };
      }
    } catch (e: any) {
      const errorCode = this.extractErrorCode(e);
      return { success: false, errorCode };
    }
  }

  async register(email: string, password: string, turnstileToken: string): Promise<AuthResult> {
    try {
      const resp: any = await lastValueFrom(
        this.http.post('/auth/register', { email, password, turnstileToken })
      );
      if (resp.success) {
        this._isLoggedIn.set(true);
        this._email.set(resp.user.email);
        this._userId.set(resp.user.id);
        this._apiKey.set(resp.user.apiKey);
        this._nicheId.set(resp.user.nicheId || ''); // fixed: camelCase
        this._locale.set(resp.user.locale || 'en');

        await this.persistSession();

        // Ensure niche data is available immediately after registration
        const nicheService = this.injector.get(NicheService);
        try {
          await nicheService.loadNiches();
        } catch (nicheErr) {
          console.error('Failed to load niches after registration', nicheErr);
        }

        if (this.pushService.ready()) {
          const platform = Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web';
          await this.pushService.registerDevice(platform);
        }
        return { success: true };
      } else {
        throw { error: { error_code: resp.error_code, error: resp.error || '' } };
      }
    } catch (e: any) {
      const errorCode = this.extractErrorCode(e);
      return { success: false, errorCode };
    }
  }

  async logout() {
    if (this.pushService.ready()) {
      await this.pushService.unregisterDevice();
    }

    try {
      await lastValueFrom(this.http.post('/auth/logout', {}));
    } catch { }

    await this.clearPersistedSession();
    this.clear();
  }

  async rotateApiKey(): Promise<boolean> {
    try {
      const resp: any = await lastValueFrom(this.http.post('/auth/rotate-api-key', {}));
      if (resp.success) {
        this._apiKey.set(resp.user.apiKey);
        await this.persistSession();
        return true;
      } else {
        throw { error: { error_code: resp.error_code, error: resp.error || '' } };
      }
    } catch (e: any) {
      const msg = this.toast.mapBackendError(e, 'errors.generic');
      this.toast.show(msg, 'error');
      return false;
    }
  }

  /**
   * Updates the persisted niche ID without triggering network calls or
   * a full session re‑authentication. This is the single source of truth
   * for the niche ID in the persistent store.
   */
  updatePersistedNicheId(nicheId: string): void {
    this._nicheId.set(nicheId);
    // Sync the updated niche ID to the persistent session payload.
    // Only non‑sensitive data is written; the API key remains untouched.
    this.persistSession();
  }

  // ──────────────────────────────────────────────
  //  Password Reset Contracts (Phase 1)
  // ──────────────────────────────────────────────

  /**
   * Contract 1: Initiate password reset.
   * Treats all 200 responses as successful, even if the email does not exist.
   */
  async requestReset(email: string, turnstileToken: string): Promise<{ success: boolean; errorCode?: string; message?: string }> {
    try {
      const resp: any = await lastValueFrom(
        this.http.post('/auth/request-password-reset', { email, turnstileToken })
      );
      return { success: true, message: resp.message || '' };
    } catch (e: any) {
      const errorCode = this.extractErrorCode(e);
      return { success: false, errorCode };
    }
  }

  /**
   * Contract 2: Validate token.
   * Returns whether the provided token is valid and unexpired.
   */
  async validateToken(token: string): Promise<{ valid: boolean; errorCode?: string }> {
    try {
      const resp: any = await lastValueFrom(
        this.http.post('/auth/validate-reset-token', { token })
      );
      return { valid: resp.valid === true };
    } catch (e: any) {
      const errorCode = this.extractErrorCode(e);
      return { valid: false, errorCode };
    }
  }

  /**
   * Contract 3: Execute password reset.
   * On success clears all local storage, cookies (via backend invalidation),
   * in‑memory signals, and redirects to the login screen.
   */
  async executeReset(token: string, newPassword: string): Promise<{ success: boolean; errorCode?: string }> {
    try {
      const resp: any = await lastValueFrom(
        this.http.post('/auth/reset-password', { token, newPassword })
      );
      if (resp.success) {
        await this.handlePasswordResetSuccess();
        return { success: true };
      } else {
        return { success: false, errorCode: resp.error_code || '' };
      }
    } catch (e: any) {
      const errorCode = this.extractErrorCode(e);
      return { success: false, errorCode };
    }
  }

  private async handlePasswordResetSuccess(): Promise<void> {
    // 1. Clear in‑memory auth signals
    this.clear();

    // 2. Remove any persisted session data (localStorage + secure Capacitor Preferences)
    await this.clearPersistedSession();

    // 3. Aggressively clear ALL remaining localStorage data
    localStorage.clear();

    // 4. Clear Capacitor secure storage (native only)
    if (Capacitor.isNativePlatform()) {
      try {
        await Preferences.clear();
      } catch {}
    }

    // 5. Redirect to login (replace URL so back button doesn't return here)
    this.router.navigate(['/login'], { replaceUrl: true });
  }

  // ──────────────────────────────────────────────

  clear() {
    this._isLoggedIn.set(false);
    this._email.set('');
    this._apiKey.set('');
    this._userId.set('');
    this._nicheId.set('');
    this._locale.set('en');
  }

  private async persistSession() {
    const payload: any = {
      email: this._email(),
      nicheId: this._nicheId(),
    };

    if (Capacitor.isNativePlatform()) {
      // 🔒 Store the API key in secure native storage
      try {
        await Preferences.set({ key: 'rtila_api_key', value: this._apiKey() });
      } catch (e) {
        console.error('Failed to store API key securely', e);
      }
    }
    // Web platform relies exclusively on HttpOnly cookies for the API key;
    // only non‑sensitive data is persisted here.
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(payload));
    } catch {}
  }

  private async clearPersistedSession() {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch {}
    if (Capacitor.isNativePlatform()) {
      try {
        await Preferences.remove({ key: 'rtila_api_key' });
      } catch {}
    }
  }

  private extractErrorCode(error: any): string {
    // Try to extract from backend response object
    const code = error?.error?.error_code || error?.error?.errorCode || error?.error_code || '';
    return code || 'UNKNOWN';
  }
}
